import React, { createContext, useEffect, useState, ReactNode } from 'react'
import { AuthValuesType, UpdateObjCtx } from './types'
import { UserDTO } from '../Types/dto/user'
import { updateObj } from '../update'
import { AuthResponse } from '../../tikexModule/Types/dto/user'
import { fetchWithBaseUrl } from '../../utils/api'

// ** Defaults
const defaultProvider: AuthValuesType = {
	userDTO: null,
	handleAuth: () => Promise.resolve(),
	handleCreateTeam: () => Promise.resolve(),
	logout: () => Promise.resolve(),
	updateUserDTO: () => Promise.resolve(),
	updateClient: () => Promise.resolve(),
	updateClientAndServer: () => Promise.resolve(),
	isUpdateUserDTOCompleted: false,
}

const AuthContext = createContext(defaultProvider)

const AuthProvider = ({
	children,
	platform,
}: {
	children: ReactNode
	platform: string
}) => {
	const [userDTO, setUserDTO] = useState<UserDTO | null>(
		defaultProvider.userDTO
	)
	const [isUpdateUserDTOCompleted, setIsUpdateUserDTOCompleted] =
		useState<Boolean>(false)

	const updateUserDTO = async (): Promise<void> => {
		const userData: string | null = localStorage.getItem('userData')
		let token = null
		if (userData) {
			const userDataObj = JSON.parse(userData)
			token = userDataObj?.token
		}
		if (token) {
			const response = await fetchWithBaseUrl('auth/me', {
				method: 'GET',
				headers: {
					Authorization: 'Bearer ' + token,
				},
			})
			if (response.ok) {
				const data: AuthResponse = await response.json()
				setUserDTO(data.data ?? null)
				localStorage.setItem('userData', JSON.stringify(data.data))
			} else {
				setUserDTO(null)
				localStorage.removeItem('userData')
			}
			setIsUpdateUserDTOCompleted(true)
		} else {
			setUserDTO(null)
			localStorage.removeItem('userData')
			setIsUpdateUserDTOCompleted(true)
		}
	}

	useEffect(() => {
		const userData: string | null = localStorage.getItem('userData')
		if (userData) {
			setUserDTO(JSON.parse(userData))
		}
		updateUserDTO()
	}, [])

	const handleAuth = async (
		url: string,
		dataReq: any,
		completion: (userDTO?: UserDTO) => void,
		isRequiredCreateTeam: Boolean
	) => {
		try {
			const response = await fetchWithBaseUrl(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(dataReq),
			})
			if (response.ok) {
				const data: AuthResponse = await response.json()
				if (data.message) {
					alert(data.message)
				} else {
					setUserDTO(data.data ?? null)
					if (isRequiredCreateTeam) {
						if (Object.keys(data.data?.teams ?? {})?.length > 0) {
						} else {
							handleCreateTeam((userDTO) => {
								localStorage.setItem('userData', JSON.stringify(userDTO))
								completion(userDTO)
							})
						}
					} else {
						localStorage.setItem('userData', JSON.stringify(data.data))
						completion(data.data)
					}
				}
			}
		} catch (e) {
			console.log(e)
			return completion()
		}
	}

	const handleCreateTeam = async (completion: (userDTO?: UserDTO) => void) => {
		try {
			const response = await fetchWithBaseUrl('createTeam', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: 'Bearer ' + userDTO?.token,
				},
			})
			if (response.ok) {
				const data: AuthResponse = await response.json()
				if (data.message) {
				} else {
					setUserDTO(data.data ?? null)
					localStorage.setItem('userData', JSON.stringify(data.data))
					completion(data.data)
				}
			}
		} catch (e) {
			console.log(e)
			return completion()
		}
	}

	useEffect(() => {
		if (platform == 'wordpress') {
			window.addEventListener('userHasLoggedIn', (event: any) => {
				const userData: string | null = localStorage.getItem('userData')
				if (userData) {
					setUserDTO(JSON.parse(userData))
				}
				updateUserDTO()
			})
			return () => {
				window.removeEventListener('userHasLoggedIn', () => {})
			}
		}
	}, [])

	const handleLogout = async (url: string) => {
		setUserDTO(null)
		window.localStorage.removeItem('userData')
	}

	const updateClient = async (params: UpdateObjCtx) => {
		const organizations2 = updateObj({
			object: userDTO?.teams,
			...params,
		})
		setUserDTO({
			...userDTO!,
			['teams']: organizations2,
		})
		//userDTO!.teams = organizations2
	}

	const updateClientAndServer = async (params: UpdateObjCtx) => {
		updateClient(params)
		const jwt = userDTO?.token
		if (jwt) {
			await fetchWithBaseUrl('update', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: 'Bearer ' + userDTO.token,
				},
				body: JSON.stringify({
					value: params.value,
					operation: params.operation,
					keyPath: params.keyPath,
				}),
			})
		}
		params.completion?.()
	}

	const values = {
		userDTO,
		handleAuth,
		handleCreateTeam,
		logout: handleLogout,
		updateUserDTO,
		updateClient,
		updateClientAndServer,
		isUpdateUserDTOCompleted,
	}

	return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
}

export { AuthContext, AuthProvider }
